import React, { useState } from "react";
import { request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import {
  DateInput,
  ErrorBox,
  Field,
  GhostButton,
  ModalShell,
  NumberInput,
  PrimaryButton,
  TextArea,
  TextInput,
} from "../../../components/FormControls";
import {
  PromoDto,
  formatDefinition,
  genderLabel,
  productLabel,
} from "../../../types/promos";
import { describeError } from "./CreateCouponModal";

type Props = {
  token: string | null;
  coupon: PromoDto;
  onClose: () => void;
  onApplied: () => void;
};

const noop = () => {};

export default function EditCouponModal({
  token,
  coupon,
  onClose,
  onApplied,
}: Props) {
  const confirm = useConfirm();
  const [endDate, setEndDate] = useState(coupon.endDate.slice(0, 10));
  const [usageLimit, setUsageLimit] = useState(
    coupon.couponUsageLimit !== null ? String(coupon.couponUsageLimit) : "",
  );
  const [conditions, setConditions] = useState(coupon.couponConditions ?? "");
  const [exceptions, setExceptions] = useState(coupon.couponExceptions ?? "");
  const [active, setActive] = useState(coupon.active);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting) return;
    if (!endDate) {
      setError("La fecha de fin es obligatoria");
      return;
    }
    if (usageLimit.trim() === "" || Number(usageLimit) <= 0) {
      setError("El límite de usos debe ser un número mayor que cero");
      return;
    }
    const ok = await confirm({
      title: "Actualizar cupón",
      message: `Se actualizará el cupón "${coupon.couponCode ?? coupon.name}". ¿Continuar?`,
      confirmLabel: "Guardar cambios",
      tone: "warning",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    try {
      await request(`/api/admin/operations/coupons/${coupon.promoId}`, {
        method: "PUT",
        token,
        body: {
          endDate,
          couponUsageLimit: Number(usageLimit),
          generalConditions: conditions,
          exceptions,
          status: active,
        },
      });
      onApplied();
    } catch (err) {
      setError(describeError(err, "No se pudo actualizar el cupón"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title={`Editar cupón · ${coupon.couponCode ?? coupon.name}`}
      onClose={onClose}
      width={600}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={submitting}>
            Cancelar
          </GhostButton>
          <PrimaryButton onClick={submit} disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar cambios"}
          </PrimaryButton>
        </>
      }
    >
      <div
        style={{
          background: "#f8f9fb",
          borderRadius: "0.4rem",
          padding: "0.6rem 0.75rem",
          marginBottom: "1rem",
          fontSize: ".78rem",
          color: "#73879C",
        }}
      >
        La definición base del cupón es inmutable (solo lectura). Solo se pueden
        editar la fecha de fin, el límite de usos, las condiciones, las
        excepciones y el estado.
      </div>

      <SectionTitle>Definición (solo lectura)</SectionTitle>
      <Row>
        <Field label="Código">
          <TextInput value={coupon.couponCode ?? "—"} onChange={noop} disabled />
        </Field>
        <Field label="Nombre">
          <TextInput value={coupon.name} onChange={noop} disabled />
        </Field>
      </Row>
      <Row>
        <Field label="Producto">
          <TextInput value={productLabel(coupon.product)} onChange={noop} disabled />
        </Field>
        <Field label="Género">
          <TextInput value={genderLabel(coupon.gender)} onChange={noop} disabled />
        </Field>
      </Row>
      <Field label="Tipo / definición de descuento">
        <TextInput value={formatDefinition(coupon)} onChange={noop} disabled />
      </Field>
      <Row>
        <Field label="Edad mínima">
          <NumberInput value={String(coupon.lowerAge)} onChange={noop} disabled />
        </Field>
        <Field label="Edad máxima">
          <NumberInput value={String(coupon.upperAge)} onChange={noop} disabled />
        </Field>
      </Row>
      <Row>
        <Field label="Precio anual mínimo">
          <NumberInput value={String(coupon.lowerPrice)} onChange={noop} disabled />
        </Field>
        <Field label="Precio anual máximo">
          <NumberInput value={String(coupon.upperPrice)} onChange={noop} disabled />
        </Field>
      </Row>
      <Field label="Descripción">
        <TextArea value={coupon.description} onChange={noop} disabled rows={2} />
      </Field>

      <SectionTitle>Editable</SectionTitle>
      <Row>
        <Field label="Fecha inicio">
          <DateInput value={coupon.startDate.slice(0, 10)} onChange={noop} disabled />
        </Field>
        <Field label="Fecha fin">
          <DateInput value={endDate} onChange={setEndDate} disabled={submitting} />
        </Field>
      </Row>
      <Field label="Límite de usos" hint={`Usos actuales: ${coupon.couponUses ?? 0}`}>
        <NumberInput value={usageLimit} onChange={setUsageLimit} disabled={submitting} min={1} />
      </Field>
      <Field label="Condiciones generales">
        <TextArea value={conditions} onChange={setConditions} disabled={submitting} rows={3} />
      </Field>
      <Field label="Excepciones">
        <TextArea value={exceptions} onChange={setExceptions} disabled={submitting} rows={2} />
      </Field>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontSize: ".85rem",
          color: "#1f2933",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          disabled={submitting}
        />
        Cupón activo
      </label>

      {error && <ErrorBox>{error}</ErrorBox>}
    </ModalShell>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: "0.4rem 0 0.7rem",
        fontSize: ".78rem",
        fontWeight: 700,
        color: "#2A3F54",
        textTransform: "uppercase",
        letterSpacing: ".4px",
        borderBottom: "2px solid #0069d9",
        paddingBottom: "0.3rem",
      }}
    >
      {children}
    </h3>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${React.Children.count(children)}, 1fr)`,
        gap: "0.7rem",
      }}
    >
      {children}
    </div>
  );
}
